// /Users/nico/Desktop/apps/portal/src/components/CommentBox.js

import React, { useState, useEffect, useMemo, useRef } from "react";
import { collection, addDoc, onSnapshot, query, orderBy, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link } from "react-router-dom";

function CommentBox({ imageId }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const commentInputRef = useRef(null);

  // Get authentication state
  const [user, loadingAuth, authError] = useAuthState(auth);

  // Fetch comments in real-time
  useEffect(() => {
    const commentsRef = collection(db, "images", imageId, "comments");
    const q = query(commentsRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedComments = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments(fetchedComments);
    });

    return () => unsubscribe();
  }, [imageId]);

  const commentThreads = useMemo(() => {
    const nodes = new Map(comments.map((comment) => [
      comment.id,
      { ...comment, replies: [] },
    ]));
    const roots = [];
    nodes.forEach((comment) => {
      const parent = comment.replyToCommentId
        ? nodes.get(comment.replyToCommentId)
        : null;
      if (parent) parent.replies.push(comment);
      else roots.push(comment);
    });
    return roots;
  }, [comments]);

  const beginReply = (comment) => {
    setReplyingTo({
      id: comment.id,
      displayName: comment.displayName || "Anonymous",
    });
    commentInputRef.current?.focus();
  };

  // Add a new comment
  const handleAddComment = async () => {
    const commentText = newComment.trim();
    if (!user || !commentText || commentText.length > 1000) return;

    const userId = user.uid;
    const displayName = user.displayName || "Anonymous";

    try {
      const commentsRef = collection(db, "images", imageId, "comments");
      const commentData = {
        text: commentText,
        userId,
        displayName,
        timestamp: serverTimestamp(),
      };
      if (replyingTo) {
        commentData.replyToCommentId = replyingTo.id;
        commentData.replyToDisplayName = replyingTo.displayName;
      }
      await addDoc(commentsRef, commentData);
      setNewComment(""); // Clear input field
      setReplyingTo(null);
    } catch (err) {
      console.error("Error adding comment:", err);
      alert("Failed to add comment. Please try again.");
    }
  };

  if (loadingAuth) {
    return <div className="text-white text-center">Loading comments...</div>;
  }

  if (authError) {
    return <div className="text-red-500 text-center">Error: {authError.message}</div>;
  }

  const renderComment = (comment, depth = 0) => (
    <div key={comment.id} className={depth > 0 ? "ml-5 border-l-2 border-slate-300 pl-3" : ""}>
      <div
        className={`p-2 mb-2 border-b border-gray-700 ${comment.entityType === "agent" ? "border-l-2 border-violet-500 pl-3" : ""}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-bold bg-gray-500 max-w-fit p-1 text-black">{comment.displayName || "Anonymous"}</p>
          {comment.entityType === "agent" && (
            <span className="rounded-full bg-violet-700 px-2 py-0.5 text-xs text-white">
              AI agent
            </span>
          )}
        </div>
        {comment.replyToDisplayName && (
          <p className="mb-1 text-xs text-gray-400">
            Replying to {comment.replyToDisplayName}
          </p>
        )}
        <p className="bg-gray-200 text-black max-w-fit p-1">{comment.text}</p>
        <div className="flex items-center gap-3 text-xs">
          <span className="italic">
            {comment.timestamp?.toDate().toLocaleString()}
          </span>
          {user && (
            <button
              type="button"
              className="font-medium text-blue-600 border-none hover:underline"
              onClick={() => beginReply(comment)}
            >
              Reply
            </button>
          )}
        </div>
      </div>
      {comment.replies.map((reply) => renderComment(reply, depth + 1))}
    </div>
  );

  return (
    <div className="mt-6">
      <h2 className="text-xl mb-4 bg-gray-200 text-black ">Comments</h2>
      
      {/* Display existing comments */}
      {commentThreads.length > 0 ? (
        commentThreads.map((comment) => renderComment(comment))
      ) : (
        <p className="text-sm italic">No comments yet. Be the first to comment!</p>
      )}

      {/* Input for adding a new comment */}
      <div className="mt-4">
        {replyingTo && (
          <div className="mb-2 flex items-center justify-between rounded bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <span>Replying to {replyingTo.displayName}</span>
            <button type="button" className="font-semibold" onClick={() => setReplyingTo(null)}>
              Cancel
            </button>
          </div>
        )}
        <textarea
          ref={commentInputRef}
          className="w-full p-2 rounded border border-gray-700 bg-gray-800 text-white"
          rows="3"
          placeholder={user ? replyingTo ? `Reply to ${replyingTo.displayName}...` : "Write a comment..." : "Sign in to comment..."}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          maxLength={1000}
          disabled={!user}
        ></textarea>
        <button
          className={`mt-2 px-4 py-2 rounded ${
            user ? "bg-blue-500 hover:bg-blue-600 text-white" : "bg-gray-500 text-gray-300 cursor-not-allowed"
          }`}
          onClick={handleAddComment}
          disabled={!user || !newComment.trim()}
          title={!user ? "Sign in to comment" : "Add Comment"}
        >
          {replyingTo ? "Add Reply" : "Add Comment"}
        </button>
        {!user && (
          <p className="text-sm text-gray-400 mt-2">
            <Link to="/auth" className="underline">
              Sign in
            </Link>{" "}
            to leave a comment.
          </p>
        )}
      </div>
    </div>
  );
}

export default CommentBox;
